import {
    createContext,
    type ReactNode,
    useContext,
    useEffect,
    useState,
} from 'react';

import { useOrganizationContext } from '@apps/web/context/organization_provider';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type OrganizationTimezoneContextType = {
    readonly timezone: string;
    readonly isLoading: boolean;
};

const OrganizationTimezoneContext =
    createContext<OrganizationTimezoneContextType>({
        timezone: 'UTC',
        isLoading: true,
    });

const STORAGE_KEY = 'phoebe-organization-timezone';

export function OrganizationTimezoneProvider({
    children,
}: {
    children: ReactNode;
}) {
    const { organizationId, memberships } = useOrganizationContext();
    const [timezone, setTimezone] = useState<string>(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem(STORAGE_KEY) || 'UTC';
        }
        return 'UTC';
    });

    const resolvedOrganizationId =
        organizationId ?? memberships[0]?.organization_id ?? null;
    const { data: organization, isLoading } = useOrganization(
        resolvedOrganizationId,
        {
            reason: 'organization-timezone-provider',
        },
    );

    // Update timezone when fresh data arrives
    useEffect(() => {
        if (organization?.timezone && organization.timezone !== timezone) {
            setTimezone(organization.timezone);
            localStorage.setItem(STORAGE_KEY, organization.timezone);
        }
    }, [organization?.timezone, timezone]);

    return (
        <OrganizationTimezoneContext.Provider value={{ timezone, isLoading }}>
            {children}
        </OrganizationTimezoneContext.Provider>
    );
}

export function useOrganizationTimezone() {
    const context = useContext(OrganizationTimezoneContext);
    if (!context) {
        throw new Error(
            'useOrganizationTimezone must be used within OrganizationTimezoneProvider',
        );
    }
    return context;
}

export function OrganizationTimezoneScope({
    timezone,
    children,
}: {
    readonly timezone: string;
    readonly children: ReactNode;
}) {
    return (
        <OrganizationTimezoneContext.Provider
            value={{ timezone, isLoading: false }}
        >
            {children}
        </OrganizationTimezoneContext.Provider>
    );
}
